import AppConfigService, { type AppConfigOptions } from '../services/appConfigService'
import { isServer } from './environment'

/**
 * Utility functions for easy access to application configuration
 * Replaces the old complex configuration utilities
 */

/**
 * Get the configuration service instance
 */
export const getConfigService = () => AppConfigService.getInstance()

let initializationPromise: Promise<void> | null = null

export const getAppConfigInitializationOptions = (): AppConfigOptions => ({
  keystoneBaseUrl:
    process.env.KEYSTONE_URL || 'https://services-sit.bees-platform.dev/v1/keystone',
  keystoneConfigKey: process.env.KEYSTONE_CONFIG_KEY || 'web-configuration-sit',
  keystoneApiKey: process.env.KEYSTONE_API_KEY,
  enableAutoUpdate: process.env.DISABLE_AUTO_UPDATE !== 'true',
  updateCheckInterval: parseInt(process.env.CONFIG_UPDATE_INTERVAL || '1800000', 10),
  idleThreshold: parseInt(process.env.IDLE_THRESHOLD || '300000', 10),
  keystoneTimeout: parseInt(process.env.KEYSTONE_TIMEOUT || '15000', 10),
  keystoneRetryCount: parseInt(process.env.KEYSTONE_RETRY_COUNT || '3', 10),
  keystoneRetryDelay: parseInt(process.env.KEYSTONE_RETRY_DELAY || '2000', 10),
})

/**
 * Initializes the in-memory config singleton in the current server process.
 * Safe to call multiple times; only one initialization runs at a time.
 */
export const ensureAppConfigInitialized = async (): Promise<void> => {
  if (!isServer) return

  const configService = getConfigService()
  const health = configService.getHealthStatus()

  if (health.isInitialized) {
    return
  }

  if (!initializationPromise) {
    initializationPromise = configService
      .initialize(getAppConfigInitializationOptions())
      .then(() => undefined)
      .catch((error) => {
        console.error('Failed to initialize AppConfigService:', error)
        throw error
      })
      .finally(() => {
        initializationPromise = null
      })
  }

  await initializationPromise
}

/**
 * Returns whether AppConfigService is already initialized in this process.
 * Does not trigger initialization.
 */
export const isAppConfigInitialized = (): boolean =>
  getConfigService().getHealthStatus().isInitialized

export const getCurrentConfig = () => getConfigService().getConfig()

/**
 * Get configuration for a specific country
 */
export const getCountryConfig = (country: string) =>
  getConfigService().getCountryConfig(country)

/**
 * Get all country configurations
 */
export const getAllCountryConfigs = () => getCurrentConfig().config

/**
 * Get domains configuration
 */
export const getDomainsConfig = () => getConfigService().getDomains()

/**
 * Get domain locale resolver function
 */
export const getDomainLocale = (context: any) =>
  getConfigService().getDomainLocale(context)

/**
 * Register user activity for intelligent update timing
 */
export const registerActivity = () => getConfigService().registerActivity()

/**
 * Get service health status for monitoring
 */
export const getAppHealthStatus = () => getConfigService().getHealthStatus()

/**
 * Graceful shutdown of the configuration service
 */
export const shutdownConfigService = () => getConfigService().shutdown()
